import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import './filterSelector.css'

// Agrupa las opciones conservando el orden en que aparece cada grupo.
const groupOptions = (options, getOptionGroup) => {
  if (!getOptionGroup) return [{ group: null, items: options }]

  const groups = []
  const groupsByKey = new Map()

  options.forEach(option => {
    const group = getOptionGroup(option) ?? null
    if (!groupsByKey.has(group)) {
      const entry = { group, items: [] }
      groupsByKey.set(group, entry)
      groups.push(entry)
    }
    groupsByKey.get(group).items.push(option)
  })

  return groups
}

/**
 * FilterSelector - Componente reutilizable de selector
 *
 * @param {string} label - Texto a mostrar en el header cuando hay una opción seleccionada que no está en options
 * @param {array} options - Array de opciones a mostrar
 * @param {function} getOptionValue - Función que retorna el valor de la opción (ej: item => item.Code)
 * @param {function} getOptionLabel - Función que retorna la etiqueta de la opción (ej: item => item.Name)
 * @param {any} value - Valor seleccionado
 * @param {function} onChange - Callback cuando se selecciona una opción. Recibe el objeto completo
 * @param {boolean} disabled - Desabilitar el selector
 * @param {string} placeholder - Texto cuando no hay selección
 * @param {string} testId
 * @param {function} searchFunction - Criterio de búsqueda definido por el padre: (option, searchText) => boolean.
 *   Si se envía, se muestra un buscador y la lista se filtra con esta función; searchText llega sin espacios
 *   al inicio ni al final y nunca vacío.
 * @param {string} searchPlaceholder - Placeholder del buscador
 * @param {string} noResultsText - Texto cuando la búsqueda no tiene resultados
 * @param {function} getOptionGroup - Función que retorna el nombre del grupo de la opción (ej: item => item.Type)
 * @param {boolean} hasError - Marca el selector con estilo de error
 */
function FilterSelector({
  label,
  options = [],
  getOptionValue,
  getOptionLabel,
  value,
  onChange,
  disabled = false,
  placeholder = 'Seleccionar',
  testId = '',
  searchFunction,
  searchPlaceholder = 'Buscar',
  noResultsText = 'No se encontraron resultados',
  getOptionGroup,
  hasError = false,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const containerRef = useRef(null)
  const searchInputRef = useRef(null)
  const isSearchable = typeof searchFunction === 'function'

  // Cerrar selector al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Limpiar la búsqueda al cerrar y enfocar el buscador al abrir
  useEffect(() => {
    if (!isOpen) {
      setSearchText('')
    } else if (isSearchable) {
      searchInputRef.current?.focus()
    }
  }, [isOpen, isSearchable])

  // Validar que las funciones existan
  if (!getOptionValue || !getOptionLabel) {
    console.error(
      'FilterSelector: getOptionValue y getOptionLabel son requeridos'
    )
    return null
  }

  const handleSelect = option => {
    onChange(option)
    setIsOpen(false)
  }

  const isOptionSelected = option => {
    return getOptionValue(option) === value
  }

  const hasMatchingOption = options.some(
    option => getOptionValue(option) === value
  )
  const isEmpty =
    (value === null || value === undefined || value === '') &&
    !hasMatchingOption

  const matchedOption = options.find(option => getOptionValue(option) === value)
  const optionLabel = matchedOption ? getOptionLabel(matchedOption) : null

  const trimmedSearch = searchText.trim()
  const filteredOptions =
    isSearchable && trimmedSearch
      ? options.filter(option => searchFunction(option, trimmedSearch))
      : options
  const optionGroups = groupOptions(filteredOptions, getOptionGroup)

  const renderOption = (option, index) => {
    const optionValue = getOptionValue(option)
    const isSelected = isOptionSelected(option)

    return (
      <li
        key={optionValue || index}
        className={`filter-selector-item ${isSelected ? 'selected' : ''}`}
        data-testid={
          testId ? `item-option-${testId}-${optionValue}` : undefined
        }
        onClick={e => {
          e.stopPropagation()
          handleSelect(option)
        }}>
        <label htmlFor={`filter-${optionValue}`}>
          {getOptionLabel(option)}
        </label>
      </li>
    )
  }

  return (
    <div
      id={testId ? `selector-${testId}` : undefined}
      className="filter-selector"
      ref={containerRef}
      data-testid={testId ? `container-selector-${testId}` : undefined}
      onClick={() => !disabled && setIsOpen(!isOpen)}>
      <div
        className={`filter-selector-header ${disabled ? 'disabled' : ''} ${
          hasError ? 'error' : ''
        }`}>
        <span
          className={`filter-label ${
            isEmpty ? 'filter-label-placeholder' : ''
          }`}>
          {!isEmpty ? optionLabel || label || placeholder : placeholder}
        </span>
        <FontAwesomeIcon
          className={`chevron ${isOpen ? 'open' : ''}`}
          icon={faChevronDown}
        />
      </div>

      {isOpen && (
        <ul
          className="filter-selector-list"
          data-testid={testId ? `list-options-${testId}` : undefined}>
          {isSearchable && (
            <li
              className="filter-selector-search"
              onClick={e => e.stopPropagation()}>
              <input
                ref={searchInputRef}
                type="text"
                className="filter-selector-search-input"
                placeholder={searchPlaceholder}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && setIsOpen(false)}
                data-testid={testId ? `input-search-${testId}` : undefined}
              />
            </li>
          )}
          {options.length === 0 ? (
            <li className="filter-selector-item disabled">
              <span>No hay opciones disponibles</span>
            </li>
          ) : filteredOptions.length === 0 ? (
            <li className="filter-selector-item disabled">
              <span>{noResultsText}</span>
            </li>
          ) : (
            optionGroups.map(({ group, items }) =>
              group === null ? (
                items.map(renderOption)
              ) : (
                <li
                  key={`group-${group}`}
                  className="filter-selector-group"
                  role="group"
                  aria-label={group}>
                  <span className="filter-selector-group-label">{group}</span>
                  <ul className="filter-selector-group-list">
                    {items.map(renderOption)}
                  </ul>
                </li>
              )
            )
          )}
        </ul>
      )}
    </div>
  )
}

export default FilterSelector
